import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, apiService } from '../services/api';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isGuest: boolean;
    permissions: string[];
    hasPermission: (permission: string) => boolean;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    setGuestToken: (token: string) => void;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    // Helper function to check if user has a specific permission
    const hasPermission = (permission: string): boolean => {
        // Admin users have all permissions
        if (user && !isGuest) {
            return true;
        }
        // Guest users check their permission list
        return permissions.includes(permission);
    };

    const checkAuthStatus = async () => {
        try {
            setLoading(true);

            // Check for admin token first
            const adminToken = localStorage.getItem('admin_token');
            if (adminToken) {
                try {
                    apiService.setAccessToken(adminToken);
                    const response = await apiService.getCurrentUser();
                    if (response.success) {
                        setUser(response.user);
                        setIsAuthenticated(true);
                        setIsGuest(false);
                        setPermissions([]); // Admin has all permissions
                        return;
                    }
                } catch (error) {
                    console.error('Invalid admin token:', error);
                    localStorage.removeItem('admin_token');
                }
            }

            // Check for access token in localStorage or URL
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get('token');
            const storedToken = localStorage.getItem('access_token');

            if (urlToken) {
                // Validate URL token
                try {
                    const response = await apiService.validateToken(urlToken);
                    if (response.success) {
                        localStorage.setItem('access_token', urlToken);
                        apiService.setAccessToken(urlToken);
                        setIsGuest(true);
                        setIsAuthenticated(true);
                        // Extract permissions from token validation response
                        const perms = response.token?.permissions ? response.token.permissions.split(',').map((p: string) => p.trim()).filter(Boolean) : ['view'];
                        setPermissions(perms);
                        // Remove token from URL for security
                        window.history.replaceState({}, document.title, window.location.pathname);
                        return;
                    }
                } catch (error) {
                    console.error('Invalid URL token:', error);
                    // Clear invalid token from URL
                    window.history.replaceState({}, document.title, window.location.pathname);
                }
            }

            if (storedToken) {
                // Validate stored token
                try {
                    const response = await apiService.validateToken(storedToken);
                    if (response.success) {
                        apiService.setAccessToken(storedToken);
                        setIsGuest(true);
                        setIsAuthenticated(true);
                        // Extract permissions from token validation response
                        const perms = response.token?.permissions ? response.token.permissions.split(',').map((p: string) => p.trim()).filter(Boolean) : ['view'];
                        setPermissions(perms);
                        return;
                    } else {
                        localStorage.removeItem('access_token');
                    }
                } catch (error) {
                    console.error('Invalid stored token:', error);
                    localStorage.removeItem('access_token');
                }
            }

            // Only check for admin session if no tokens were found
            // Skip this check to speed up initial load - user can login if needed
            // This prevents the 504 timeout on initial page load
            // If there was a valid admin session, it would have been caught by the admin_token check above

            // If we reach here, no authentication was found
            setIsAuthenticated(false);
            setIsGuest(false);
            setUser(null);
            setPermissions([]);

        } catch (error) {
            console.error('Auth check failed:', error);
            setIsAuthenticated(false);
            setIsGuest(false);
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    };

    const login = async (username: string, password: string): Promise<boolean> => {
        try {
            const response = await apiService.login(username, password);

            if (response.success) {
                setUser(response.user);
                setIsAuthenticated(true);
                setIsGuest(false);
                setPermissions([]); // Admin has all permissions

                // Store admin token if provided
                if (response.token) {
                    localStorage.setItem('admin_token', response.token);
                    apiService.setAccessToken(response.token);
                }

                // Clear any guest token
                localStorage.removeItem('access_token');
                return true;
            }
            return false;
        } catch (error) {
            console.error('Login failed:', error);
            return false;
        }
    };

    const logout = async () => {
        try {
            if (!isGuest) {
                await apiService.logout();
            }
            setUser(null);
            setIsAuthenticated(false);
            setIsGuest(false);
            setPermissions([]);
            localStorage.removeItem('access_token');
            localStorage.removeItem('admin_token');
            apiService.setAccessToken(null);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const setGuestToken = (token: string) => {
        localStorage.setItem('access_token', token);
        apiService.setAccessToken(token);
        setIsGuest(true);
        setIsAuthenticated(true);
        // Default to view permissions, will be updated when token is validated
        setPermissions(['view']);
    };

    const value: AuthContextType = {
        user,
        isAuthenticated,
        isGuest,
        permissions,
        hasPermission,
        login,
        logout,
        setGuestToken,
        loading,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};